import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Upload,
  Loader2,
  Scale,
  FileSearch,
  ShieldCheck,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  createLabourAnalysis,
  listMyLabourAnalyses,
  runLabourAnalysis,
  exportLabourAnalysisPdf,
} from "@/lib/api/labour-analyzer.functions";
import { downloadBase64File } from "@/lib/download-base64";
import { getMyProfile } from "@/lib/api/portal.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CcmaFormsGuide } from "@/components/labour/CcmaFormsGuide";

export const Route = createFileRoute("/_authenticated/portal/labour-analyzer")({
  head: () => ({
    meta: [
      { title: "AI Labour Law Analyzer | NDLCS Client Portal" },
      {
        name: "description",
        content:
          "Upload employment contracts, charge sheets, dismissal letters and CCMA documents for instant AI-powered South African labour law analysis.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LabourAnalyzerPage,
});

const DOC_TYPES = [
  { v: "employment_contract", l: "Employment contract" },
  { v: "disciplinary_charge_sheet", l: "Disciplinary charge sheet" },
  { v: "hearing_notice", l: "Hearing notice" },
  { v: "hearing_outcome", l: "Hearing outcome" },
  { v: "dismissal_letter", l: "Dismissal letter" },
  { v: "retrenchment_notice", l: "Retrenchment / s189 notice" },
  { v: "warning_letter", l: "Warning letter" },
  { v: "grievance", l: "Grievance document" },
  { v: "settlement_agreement", l: "Settlement agreement" },
  { v: "ccma_form", l: "CCMA form / award / ruling" },
  { v: "other", l: "Other employment document" },
] as const;

const ACCEPTED = ".pdf,.docx,.doc,.txt,.png,.jpg,.jpeg,.webp";
const MAX_BYTES = 20 * 1024 * 1024;

function bytesPretty(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    analyzing: { label: "AI analysing…", cls: "bg-amber-100 text-amber-900" },
    admin_review: { label: "Awaiting admin review", cls: "bg-sky-100 text-sky-900" },
    approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-900" },
    rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-900" },
    archived: { label: "Archived", cls: "bg-muted text-muted-foreground" },
    error: { label: "Error", cls: "bg-rose-100 text-rose-900" },
  };
  const m = map[status] ?? { label: status, cls: "bg-muted text-muted-foreground" };
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${m.cls}`}>
      {m.label}
    </span>
  );
}

function LabourAnalyzerPage() {
  const qc = useQueryClient();
  const fetchProfile = useServerFn(getMyProfile);
  const fetchList = useServerFn(listMyLabourAnalyses);
  const create = useServerFn(createLabourAnalysis);
  const runAi = useServerFn(runLabourAnalysis);
  const exportPdf = useServerFn(exportLabourAnalysisPdf);
  const [pdfBusy, setPdfBusy] = useState<string | null>(null);

  async function downloadPdf(id: string) {
    setPdfBusy(id);
    try {
      const { base64, filename } = await exportPdf({ data: { id } });
      downloadBase64File(base64, filename);
    } catch (e: any) {
      toast.error(e?.message ?? "Download failed");
    } finally {
      setPdfBusy(null);
    }
  }

  const { data: _prof } = useQuery({
    queryKey: ["my-profile"],
    queryFn: () => fetchProfile(),
  });
  const { data: listData, isLoading } = useQuery({
    queryKey: ["my-labour-analyses"],
    queryFn: () => fetchList(),
  });

  const [matter, setMatter] = useState("");
  const [docType, setDocType] = useState<string>("dismissal_letter");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return toast.error("Please choose a file.");
    if (file.size > MAX_BYTES) return toast.error("File is larger than 20 MB.");
    if (matter.trim().length < 3) return toast.error("Give the matter a short title.");

    setUploading(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData.user?.id;
      if (!uid) throw new Error("Not signed in");
      const cleanName = file.name.replace(/[^\w.\-]+/g, "_").slice(0, 100);
      const path = `${uid}/${crypto.randomUUID()}-${cleanName}`;

      const up = await supabase.storage
        .from("labour-documents")
        .upload(path, file, { contentType: file.type || "application/octet-stream" });
      if (up.error) throw new Error(up.error.message);

      const { id } = await create({
        data: {
          storagePath: path,
          originalFilename: file.name,
          mimeType: file.type || null,
          sizeBytes: file.size,
          matterTitle: matter.trim(),
          docType: docType as any,
        },
      });

      toast.success("Uploaded — AI is analysing your matter now…");
      qc.invalidateQueries({ queryKey: ["my-labour-analyses"] });
      // Kick off AI in background
      runAi({ data: { id } })
        .then(() => {
          toast.success("AI analysis complete — sent for admin review");
          qc.invalidateQueries({ queryKey: ["my-labour-analyses"] });
          qc.invalidateQueries({ queryKey: ["labour-analysis", id] });
        })
        .catch((e: any) => toast.error(e?.message ?? "AI analysis failed"));

      setMatter("");
      setFile(null);
    } catch (e: any) {
      toast.error(e?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  const rows = listData?.rows ?? [];

  return (
    <div className="space-y-8">
      <section className="rounded-2xl border border-border bg-gradient-to-br from-navy/[0.04] to-gold/[0.06] p-6 shadow-soft">
        <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold">
          <Scale className="h-4 w-4" /> AI Labour Law Analyzer
        </div>
        <h2 className="font-display text-2xl text-navy">
          Read, analyse and prepare a labour-law response in minutes
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Upload your employment contract, charge sheet, hearing notice, dismissal letter,
          retrenchment notice, warning letter, grievance, settlement agreement or CCMA document.
          Our AI applies the LRA, BCEA, EEA and Schedule 8 to flag procedural and substantive
          issues, identify CCMA opportunities, and draft Form 7.11 referrals, representations,
          mitigation statements, appeals and grievances for you. Every report is reviewed and
          signed-off by an NDLCS administrator before it is treated as final.
        </p>
        <p className="mt-3 text-[11px] text-muted-foreground">
          Files stored privately on Lovable Cloud · POPIA-compliant · Legal support only — not
          legal representation.
        </p>
      </section>

      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded-xl border border-border bg-card p-6 shadow-soft"
      >
        <div className="flex items-center gap-2 text-sm font-semibold text-navy">
          <Upload className="h-4 w-4 text-gold" /> Upload a matter
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="matter">Matter title</Label>
            <Input
              id="matter"
              value={matter}
              onChange={(e) => setMatter(e.target.value)}
              placeholder="e.g. Dismissal — Spar Polokwane — 12 June 2026"
              maxLength={200}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label>Document type</Label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((t) => (
                  <SelectItem key={t.v} value={t.v}>
                    {t.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="file">File (PDF, DOCX, JPG/PNG, TXT — max 20 MB)</Label>
          <Input
            id="file"
            type="file"
            accept={ACCEPTED}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            required
          />
          {file && (
            <p className="text-xs text-muted-foreground">
              {file.name} · {bytesPretty(file.size)}
            </p>
          )}
        </div>

        <Button type="submit" disabled={uploading} className="bg-navy hover:bg-navy/90">
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…
            </>
          ) : (
            <>Upload & start AI analysis</>
          )}
        </Button>

        <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-3 w-3 text-emerald-700" />
          Your file is encrypted at rest, scoped to your account, and only NDLCS administrators can
          review it. Do not paste another person's confidential documents without their consent.
        </p>
      </form>

      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-navy">
          <FileSearch className="h-4 w-4 text-gold" /> My matters
        </div>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-muted/30 p-6 text-sm text-muted-foreground">
            No matters yet. Upload your first employment document above.
          </p>
        ) : (
          <ul className="space-y-2">
            {rows.map((r: any) => (
              <li
                key={r.id}
                className="rounded-lg border border-border bg-card p-4 hover:border-navy"
              >
                <div className="flex items-start justify-between gap-3">
                  <Link
                    to={"/portal/labour-analyzer/$id" as any}
                    params={{ id: r.id } as any}
                    className="min-w-0 flex-1"
                  >
                    <p className="truncate text-sm font-semibold text-navy">
                      {r.matter_title}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {r.doc_type.replaceAll("_", " ")} · {r.original_filename} ·{" "}
                      {new Date(r.created_at).toLocaleDateString("en-ZA")}
                    </p>
                    {r.ai_summary && (
                      <p className="mt-1 line-clamp-2 text-xs text-foreground/80">
                        {r.ai_summary}
                      </p>
                    )}
                  </Link>
                  <div className="flex shrink-0 items-center gap-3">
                    {r.ai_confidence !== null && r.ai_confidence !== undefined && (
                      <span className="text-[10px] text-muted-foreground">
                        Confidence {r.ai_confidence}%
                      </span>
                    )}
                    <StatusBadge status={r.status} />
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </div>
                </div>
                {r.status === "approved" && (
                  <div className="mt-3 border-t border-border/60 pt-3">
                    <button
                      type="button"
                      onClick={() => downloadPdf(r.id)}
                      disabled={pdfBusy === r.id}
                      className="inline-flex items-center gap-1.5 rounded-md border border-emerald-700 bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700/90 disabled:opacity-60"
                    >
                      {pdfBusy === r.id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Download className="h-3.5 w-3.5" />
                      )}
                      Download approved report (PDF)
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <CcmaFormsGuide />

      <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">

        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          <strong>Disclaimer:</strong> NDLCS provides legal support and guidance, not attorney-client
          legal representation. AI-generated reports are reviewed by an administrator but you remain
          responsible for verifying facts and meeting CCMA / court deadlines.
        </span>
      </p>
    </div>
  );
}
